import { mainProductService } from '../services/mainProductService';
import { productService } from '../services/productService';
import { referenceDataSyncService } from '../../services/referenceDataSyncService';

export class ProductRepository {
  async fetchMainProductsOnline(): Promise<any[]> {
    try {
      const result = await mainProductService.getMainProductList({});
      if (result?.statusText === 'SUCCESS' && result?.data?.data?.data) {
        const list = result?.data?.data?.data;
        const sorted = list?.sort((a: any, b: any) => (a.name || '').localeCompare(b.name || ''));
        return sorted;
      }
    } catch (error) {
      console.warn('Failed to load main products online in background', error);
    }
    return [];
  }

  async loadMainProducts(
    isOnline: boolean,
    onUpdate?: (mainProducts: any[]) => void,
    onLoadingComplete?: () => void
  ): Promise<any[]> {
    const offline = await referenceDataSyncService.getMainProducts();
    const initialData = offline || [];

    if (initialData?.length > 0 && onUpdate) {
      onUpdate(initialData);
    }

    const shouldFetchOnline = isOnline;

    if (shouldFetchOnline) {
      this.fetchMainProductsOnline()
        .then((data) => {
          if (data && data?.length > 0) {
            if (onUpdate) {
              onUpdate(data);
            }
          }
        })
        .catch((error) => {
          console.warn('Failed to load main products online in background', error);
        })
        .finally(() => {
          if (onLoadingComplete) {
            onLoadingComplete();
          }
        });
    } else {
      if (onLoadingComplete) {
        onLoadingComplete();
      }
    }

    return initialData;
  }

  async fetchPresentationsOnline(): Promise<any[]> {
    try {
      const response = await productService.getProducts({ limit: 50 });
      if (response && response.data) {
        return response?.data;
      }
    } catch (error) {
      console.warn('Failed to load presentations online in background', error);
    }
    return [];
  }

  async loadPresentations(
    isOnline: boolean,
    onUpdate?: (presentations: any[]) => void,
    onLoadingComplete?: () => void
  ): Promise<any[]> {
    const offline = await referenceDataSyncService.getPresentations();
    const initialData = offline || [];

    if (initialData?.length > 0 && onUpdate) {
      onUpdate(initialData);
    }

    const shouldFetchOnline = isOnline;

    if (shouldFetchOnline) {
      this.fetchPresentationsOnline()
        .then((data) => {
          if (data && data?.length > 0) {
            if (onUpdate) {
              onUpdate(data);
            }
          }
        })
        .catch((error) => {
          console.warn('Failed to load presentations online in background', error);
        })
        .finally(() => {
          if (onLoadingComplete) {
            onLoadingComplete();
          }
        });
    } else {
      if (onLoadingComplete) {
        onLoadingComplete();
      }
    }

    return initialData;
  }

  checkDuplicatePresentation(
    presentations: any[],
    {
      mainProductId,
      brandId,
      brandStr,
      presentationId,
      presentationStr,
      excludeRefId,
    }: {
      mainProductId: number;
      brandId: number | null;
      brandStr: string;
      presentationId: number | null;
      presentationStr: string;
      excludeRefId?: string;
    }
  ): boolean {
    const formCustomBrandStr = (brandStr || '').trim().toLowerCase();
    const formCustomPresentationStr = (presentationStr || '').trim().toLowerCase();

    return presentations.some((prod) => {
      if (excludeRefId && prod.refId === excludeRefId) {
        return false;
      }

      // Check Main Product Match
      const prodMainProductId = prod.mainProductId || prod.mainProduct?.id;
      const isMainProductMatch = prodMainProductId === mainProductId;

      // Check Brand Match
      let isBrandMatch = false;
      const prodBrandId = prod.brandId || prod.productBrand?.id;
      if (brandId) {
        isBrandMatch = prodBrandId === brandId;
      } else {
        const prodBrandStr = (prod.brand || prod.productBrand?.name || '').trim().toLowerCase();
        isBrandMatch = prodBrandStr === formCustomBrandStr;
      }

      // Check Presentation Match
      let isPresentationMatch = false;
      const prodPresentationId = prod.presentationId || prod.productPresentation?.id;
      if (presentationId) {
        isPresentationMatch = prodPresentationId === presentationId;
      } else {
        const prodPresentationStr = (prod.presentation || prod.productPresentation?.name || '')
          .trim()
          .toLowerCase();
        isPresentationMatch = prodPresentationStr === formCustomPresentationStr;
      }

      return isMainProductMatch && isBrandMatch && isPresentationMatch;
    });
  }

  checkBarcodeDuplicate(presentations: any[], barcode: string, excludeRefId?: string): boolean {
    if (!barcode || barcode.trim() === '') {
      return false;
    }
    const cleanBarcode = barcode.trim();
    return presentations.some((prod) => {
      if (!prod?.barcode) return false;
      if (excludeRefId && prod?.refId === excludeRefId) return false;
      return prod?.barcode?.trim() === cleanBarcode;
    });
  }

  async loadPresentationsByMainProduct(
    isOnline: boolean,
    mainProductId: number | string,
    onUpdate?: (presentations: any[]) => void,
    onLoadingComplete?: () => void
  ): Promise<any[]> {
    const offlineResponse = await referenceDataSyncService.getPresentations();
    const filteredOffline = (offlineResponse || []).filter((product: any) => {
      const mainId = product?.mainProductId || product?.mainProduct?.id || product?.mainProduct;
      return String(mainId) === String(mainProductId);
    });

    if (filteredOffline?.length > 0 && onUpdate) {
      onUpdate(filteredOffline);
    }

    if (isOnline) {
      productService
        .getProductByMainProductId(mainProductId)
        .then((response) => {
          if (response) {
            const sortedOnline = response.sort((a: any, b: any) =>
              (a.name || a.productName || '').localeCompare(b.name || b.productName || '')
            );
            if (onUpdate) {
              onUpdate(sortedOnline);
            }
          }
        })
        .catch((error) => {
          console.warn('Failed to load presentations by main product online', error);
        })
        .finally(() => {
          if (onLoadingComplete) {
            onLoadingComplete();
          }
        });
    } else {
      if (onLoadingComplete) {
        onLoadingComplete();
      }
    }

    return filteredOffline;
  }

  filterMainProductsByCategory(mainProducts: any[], categoryIds: number[]): any[] {
    if (!categoryIds || categoryIds?.length === 0) {
      return mainProducts;
    }
    return mainProducts.filter((p: any) => {
      const catId = p?.productCategoryId || p?.productCategory?.id || p?.productCategory;
      return categoryIds.includes(Number(catId));
    });
  }
}

export const productRepository = new ProductRepository();
